/**
 * G4-T25: Offline Sync yönetim sayfası.
 * /sync rotasında: bekleyen kuyruğu görüntüle, manuel flush, delta çek.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Button, Table, Badge, Alert, Spinner } from 'reactstrap';
import syncManager from '../utils/syncManager';
import db from '../utils/syncDb';
import { getToken } from '../utils/auth';

function getAuthToken() {
  return getToken();
}

export default function SyncPage() {
  const [queueItems,   setQueueItems]   = useState([]);
  const [lastSync,     setLastSync]     = useState(null);
  const [isOnline,     setIsOnline]     = useState(navigator.onLine);
  const [isFlushing,   setIsFlushing]   = useState(false);
  const [flushResult,  setFlushResult]  = useState(null);
  const [deltaResult,  setDeltaResult]  = useState(null);

  const loadQueue = useCallback(async () => {
    const items = await db.syncQueue.orderBy('id').reverse().limit(100).toArray();
    setQueueItems(items);
    const last = await syncManager.getLastSyncTime();
    setLastSync(last);
  }, []);

  useEffect(() => {
    loadQueue();
    const onOnline  = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    window.addEventListener('online',  onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online',  onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [loadQueue]);

  async function handleFlush() {
    setIsFlushing(true);
    setFlushResult(null);
    const token = getAuthToken();
    if (!token) {
      setFlushResult({ error: 'Auth token bulunamadı. Lütfen giriş yapın.' });
      setIsFlushing(false);
      return;
    }
    const result = await syncManager.flushQueue(token);
    setFlushResult(result);
    setIsFlushing(false);
    await loadQueue();
  }

  async function handlePullDelta() {
    const token = getAuthToken();
    if (!token) {
      setDeltaResult({ error: 'Auth token bulunamadı.' });
      return;
    }
    const changes = await syncManager.pullDelta(token);
    setDeltaResult({ count: changes.length, changes });
    await loadQueue();
  }

  async function handleClearSent() {
    await db.syncQueue.where('status').anyOf(['sent', 'failed']).delete();
    await loadQueue();
  }

  function statusBadge(status) {
    const map = {
      pending:  'warning',
      sent:     'success',
      conflict: 'danger',
      failed:   'secondary',
    };
    return <Badge color={map[status] || 'light'}>{status}</Badge>;
  }

  return (
    <div className="mt-3">
      <h4>Offline Sync — Kuyruk Yönetimi</h4>

      <div className="d-flex align-items-center gap-3 mb-3">
        <Badge color={isOnline ? 'success' : 'danger'} pill>
          {isOnline ? 'Çevrimiçi' : 'Çevrimdışı'}
        </Badge>
        {lastSync && (
          <small className="text-muted">
            Son sync: {new Date(lastSync).toLocaleTimeString('tr-TR')}
          </small>
        )}
      </div>

      <div className="d-flex gap-2 mb-3 flex-wrap">
        <Button color="primary" size="sm" onClick={handleFlush} disabled={isFlushing || !isOnline}>
          {isFlushing ? <><Spinner size="sm" /> Gönderiliyor…</> : 'Kuyruğu Gönder'}
        </Button>
        <Button color="outline-secondary" size="sm" onClick={handlePullDelta} disabled={!isOnline}>
          Değişiklikleri Çek (Delta)
        </Button>
        <Button color="outline-danger" size="sm" onClick={handleClearSent}>
          Gönderilenleri Temizle
        </Button>
        <Button color="outline-primary" size="sm" onClick={loadQueue}>
          Yenile
        </Button>
      </div>

      {flushResult && (
        <Alert color={flushResult.error ? 'danger' : 'info'} className="py-2">
          {flushResult.error
            ? flushResult.error
            : `Gönderildi: ${flushResult.processed}  |  Çakışma: ${flushResult.conflicts}  |  Başarısız: ${flushResult.failed}`}
        </Alert>
      )}

      {deltaResult && (
        <Alert color="info" className="py-2">
          {deltaResult.error
            ? deltaResult.error
            : `Delta: ${deltaResult.count} değişiklik alındı`}
        </Alert>
      )}

      {queueItems.length === 0 ? (
        <p className="text-muted">Kuyruk boş.</p>
      ) : (
        <Table size="sm" bordered hover responsive>
          <thead className="table-light">
            <tr>
              <th>#</th>
              <th>Tür</th>
              <th>ID</th>
              <th>İşlem</th>
              <th>Zaman</th>
              <th>Durum</th>
              <th>Deneme</th>
            </tr>
          </thead>
          <tbody>
            {queueItems.map(item => (
              <tr key={item.id}>
                <td>{item.id}</td>
                <td>{item.entityType}</td>
                <td>{item.entityId}</td>
                <td>{item.operation}</td>
                <td>{new Date(item.clientTimestamp).toLocaleString('tr-TR')}</td>
                <td>{statusBadge(item.status)}</td>
                <td>{item.retries}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}
